import { useActionState, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { User, loginUser } from '@/tmp';
import { isPhoneOrHighScreen, isRunningInApp } from '@/utilities/sizeUtils';
import { PlutoClientException } from '@/PlutoClientException';
import { LoginForm } from './LoginForm';
import { saveUsernamePassword } from '@/components/flutter/SaveUsernamePassword';
import { BiometricLoginPage } from './BiometricLoginPage';
import { BiometicLoginSupport, State } from '@/types';
import { areCredentialsSaved } from '@/components/flutter/BiometricLogin';

import './LoginPage.css';
import { createInitState } from '@/utilities/formUtils';

export type LoginFields = {
  username: string;
  password: string;
  rememberMe?: string;
};

export default function LoginPage({ loggedIn, resetPw }: { loggedIn: (user: User) => void; resetPw: () => void }) {
  const { t } = useTranslation();
  const loginState = createInitState<LoginFields>(['username', 'password']);
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const [state, formAction] = useActionState(tryLogin, loginState);
  const [rememberMePreviousValue, setRememberMePreviousValue] = useState<boolean>(true);

  // azert kell ez az initial value, hogy meg tudjuk varni a areCredentialsSaved eredmenyet
  // useEffect nem var :(
  const [bio, setBio] = useState<BiometicLoginSupport>('dunno');

  function readSavedBio(): BiometicLoginSupport {
    const biometric = localStorage.getItem('biometriclogin');
    if (biometric === null) return 'notavailable';
    if (biometric === 'notsupported') return 'notsupported';
    if (biometric === 'off') return 'off';
    if (biometric === 'notavailable') return 'notavailable'; // nincs elmentve az usernev/jelszo
    return biometric === 'true' ? 'true' : 'false';
  }

  // cookiebot
  // TODO ez tul bonyi lett
  // ez osszes localstore cucc egybe
  // bio + remember me
  useEffect(() => {
    async function initBio() {
      const saved = await areCredentialsSaved();
      const savedBio = readSavedBio();
      if (!saved) {
        if (bio === 'true' || bio === 'false') {
          setBio('notavailable');
          localStorage.setItem('biometriclogin', 'notavailable');
        } else {
          setBio(savedBio);
        }
      } else {
        if (bio === 'notavailable') {
          setBio('true');
          localStorage.setItem('biometriclogin', 'true');
        } else {
          setBio(savedBio);
        }
      }
    }
    void initBio();
  }, []);

  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  async function login(username: string, password: string, rememberMe: boolean, isBiometricLogin: boolean) {
    const user = await loginUser({ username, password, rememberMe, isBiometricLogin }); // call server to login
    loggedIn(user); // set global state to loggedIn which will take the user to the Home screen
    localStorage.setItem('rememberme', rememberMe ? 'true' : 'false');
    setRememberMePreviousValue(rememberMe);
  }

  async function tryLogin(prev: State<LoginFields>, formData: FormData): Promise<State<LoginFields>> {
    const formPayload = Object.fromEntries(formData) as LoginFields;
    const missingUsername = !formPayload.username;
    const missingPassword = !formPayload.password;
    if (missingUsername || missingPassword) {
      return {
        globalError: undefined,
        errors: {
          username: missingUsername ? t('login.error.missingUsername') : undefined,
          password: missingPassword ? t('login.error.missingPassword') : undefined,
        },
      };
    }
    try {
      await login(formPayload.username, formPayload.password, formPayload.rememberMe === 'on', false);
      saveUsernamePassword(formPayload.username, formPayload.password);
      const bio = localStorage.getItem('biometriclogin');
      if (bio === 'notavailable') localStorage.setItem('biometriclogin', 'true');
      return prev;
    } catch (error) {
      if (!(error instanceof PlutoClientException)) {
        return {
          globalError: t('error.api.login'),
          errors: { username: undefined, password: undefined },
        };
      }

      switch (error.statusCode) {
        case 403:
          return {
            globalError: t('error.api.err_prohibited'),
            errors: { username: undefined, password: undefined },
          };

        default:
          return loginState; // no error is shown, notification is displayed
      }
    }
  }

  function startBiometric() {
    localStorage.setItem('biometriclogin', 'true');
    setBio('true');
  }

  function startNormal(notsupported: boolean) {
    if (notsupported) {
      localStorage.setItem('biometriclogin', 'notsupported');
      setBio('notsupported');
    } else {
      localStorage.setItem('biometriclogin', 'false');
      setBio('false');
    }
  }

  if (bio === 'dunno') return null;
  if (bio === 'true') return <BiometricLoginPage login={login} startNormal={startNormal} />;

  return (
    <LoginForm
      rememberMePreviousValue={rememberMePreviousValue}
      isPhone={isPhone}
      state={state}
      formAction={formAction}
      canBiometric={isRunningInApp() && bio === 'false'}
      startBiometric={startBiometric}
      resetPw={resetPw}
    />
  );
}
